import { useCallback, useMemo } from 'react'
import { Ban, Bot, Check } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchEvents, resolveEvent, FRAUD_STR as S, type SuspiciousEventRow } from './fraudApi'

/**
 * A25.11 — Синтетическая активность. Scored signals of bot/synthetic behaviour
 * from the REAL `/admin/antifraud/events` feed; block the account or clear the
 * signal (false positive). Placeholder permission USERS_VIEW.
 */
export function SyntheticActivityScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<SuspiciousEventRow>[]>(
    () => [
      { key: 'user', header: S.user, type: 'text' },
      { key: 'type', header: S.type, type: 'text' },
      { key: 'score', header: S.score, type: 'risk' },
      { key: 'reason', header: S.reason, type: 'text' },
      {
        key: 'status',
        header: S.status,
        type: 'enum',
        options: [
          { value: 'open', label: S.openCount, status: 'open' },
          { value: 'blocked', label: S.blocked, status: 'blocked' },
          { value: 'cleared', label: S.cleared, status: 'active' },
        ],
      },
      { key: 'created_at', header: S.created, type: 'text' },
    ],
    [],
  )

  const resolve = useCallback(
    async (row: SuspiciousEventRow, action: 'block' | 'clear') => {
      try {
        await resolveEvent(row.id, action)
        toast(action === 'block' ? S.blocked : S.cleared, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<SuspiciousEventRow>[]>(
    () => [
      {
        id: 'block',
        label: S.block,
        icon: <Ban size={14} />,
        danger: true,
        visible: (r) => r.status === 'open',
        onSelect: (r) => resolve(r, 'block'),
      },
      {
        id: 'clear',
        label: S.clear,
        icon: <Check size={14} />,
        visible: (r) => r.status === 'open',
        onSelect: (r) => resolve(r, 'clear'),
      },
    ],
    [resolve],
  )

  const fetcher = useCallback(fetchEvents, [])
  return (
    <>
      <PageHeader title={S.synthetic} screenId="A25.11" />
      <DataTable<SuspiciousEventRow>
        screenId="A25.11"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        emptyState={{ icon: <Bot size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
